/**
 * Composing binomial names from an identification taxonomy (genus + species).
 *
 * The AI provider sometimes returns the full binomial in `species`
 * (`{ genus: 'Zebrasoma', species: 'Zebrasoma flavescens' }`), and composing
 * `${genus} ${species}` then saved "Zebrasoma Zebrasoma flavescens". These helpers
 * never repeat the genus. Kept identical to src/utils/scientificName.ts (backend).
 *
 * Tautonyms are valid names and stay untouched: `Anthias anthias` is genus
 * "Anthias" + epithet "anthias" (lower case), not a duplicated genus.
 */

export interface TaxonomyParts {
  family?: string | undefined;
  genus?: string | undefined;
  species?: string | undefined;
}

/**
 * The species epithet without a leading copy of the genus
 * (`('Zebrasoma', 'Zebrasoma flavescens')` → `'flavescens'`). Case-insensitive on
 * the genus, and only when something follows it — `('Anthias', 'anthias')` stays.
 */
export function speciesEpithet(genus: string | undefined, species: string | undefined): string {
  const g = (genus ?? '').trim();
  let s = (species ?? '').trim().replace(/\s+/g, ' ');
  if (!g) return s;
  const prefix = `${g.toLowerCase()} `;
  // Strip repeatedly: "Zebrasoma Zebrasoma flavescens" in `species` is the same bug twice.
  while (s.toLowerCase().startsWith(prefix) && s.length > prefix.length) {
    s = s.slice(prefix.length).trim();
  }
  return s;
}

/**
 * The scientific name at the depth the taxonomy actually has: "Genus species",
 * "Genus sp." or the family — the genus never twice.
 */
export function composeScientificName(taxonomy: TaxonomyParts | null | undefined): string {
  const genus = taxonomy?.genus?.trim() ?? '';
  const species = speciesEpithet(genus, taxonomy?.species);
  if (genus && species) return `${genus} ${species}`;
  if (genus) return `${genus} sp.`;
  return taxonomy?.family?.trim() ?? '';
}

/**
 * Guard for a free-text scientific name: collapses a duplicated, capitalised genus
 * at the start ("Zebrasoma Zebrasoma flavescens" → "Zebrasoma flavescens").
 * Conservative on purpose — it only applies when the first two words are the same
 * capitalised word (case-sensitive) and a third word follows, so tautonyms such as
 * "Anthias anthias" or "Gorilla gorilla gorilla" are never changed.
 */
export function dedupeLeadingGenus(name: string): string {
  const words = name.trim().split(/\s+/);
  const genus = words[0] ?? '';
  if (!/^[A-Z][a-z]+$/.test(genus)) return name;
  // Skip the repeated genus words, but always keep at least one word after the genus.
  let next = 1;
  while (next < words.length - 1 && words[next] === genus) next += 1;
  return next === 1 ? name : [genus, ...words.slice(next)].join(' ');
}
